/**
 * Network (My Network) API. Calls /me/network endpoints.
 * Uses VITE_API_BASE_URL and auth token from localStorage.
 */

const AWS_API_BASE_URL_FALLBACK = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
const API_BASE = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || AWS_API_BASE_URL_FALLBACK;

function base(path) {
  const p = String(path || '').startsWith('/') ? path : `/${path}`;
  return `${API_BASE.replace(/\/+$/, '')}${p}`;
}

function getToken() {
  try {
    const raw = localStorage.getItem('user');
    const u = raw ? JSON.parse(raw) : null;
    return u?.tokens?.idToken || u?.tokens?.IdToken || u?.idToken || u?.IdToken || '';
  } catch {
    return '';
  }
}

function headers(auth = true) {
  const h = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = getToken();
    if (t) h.Authorization = `Bearer ${t}`;
  }
  return h;
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  let res;
  try {
    res = await fetch(base(path), {
      method,
      headers: headers(auth),
      body: body != null ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    const msg = e?.message || 'Failed to fetch';
    throw new Error(msg.includes('fetch') ? 'Network error. Check that you are signed in and the API is available (VITE_API_BASE_URL), then try again.' : msg);
  }
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `API Error: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const networkAPI = {
  /** List current user's connections (My Network) */
  async getMyNetwork() {
    const data = await request('/me/network', { method: 'GET' });
    return data?.connections || [];
  },

  /** List pending connection requests (invitations - people who want to connect with me) */
  async getInvitations() {
    const data = await request('/me/network/invitations', { method: 'GET' });
    return data?.invitations || [];
  },

  /** Check if current user is connected to the given user_id */
  async checkConnected(userId) {
    if (!userId) return false;
    const data = await request(`/me/network/check/${encodeURIComponent(userId)}`, { method: 'GET' });
    return !!data?.connected;
  },

  /** Send connection REQUEST (not automatic connect; recipient accepts in My Network > Invitations) */
  async connect(userId) {
    if (!userId) throw new Error('user_id required');
    await request('/me/network', { method: 'POST', body: { user_id: userId } });
  },

  /** Accept a connection request (from_user_id = person who sent the request) */
  async acceptRequest(fromUserId) {
    if (!fromUserId) throw new Error('user_id required');
    await request('/me/network/accept', { method: 'POST', body: { user_id: fromUserId } });
  },

  /** Ignore a connection request */
  async ignoreRequest(fromUserId) {
    if (!fromUserId) throw new Error('user_id required');
    await request('/me/network/ignore', { method: 'POST', body: { user_id: fromUserId } });
  },

  /** Remove connection (removes from My Network) */
  async disconnect(userId) {
    if (!userId) throw new Error('user_id required');
    await request(`/me/network/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  },
};
